// Reporting week start day, configured per company in Financial Settings
// (CompanyFinancials.weekStartDay) and honored by every weekly grouping.
//
// The granularity names returned by `weekGranularityName` MUST stay in sync
// with two mirrors that cannot import this module:
//   - `cube/utils/weekGranularities.js` (custom granularity definitions on
//     every cube time dimension; Cube Cloud deploys only the cube/ directory)
//   - `cube/cube.js` queryRewrite (maps granularity 'week' to the company's
//     custom granularity based on the weekStartDay JWT claim)

export const WEEK_START_DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;

export type WeekStartDay = (typeof WEEK_START_DAYS)[number];

// Monday keeps Cube's built-in ISO week granularity, so companies that never
// touch the setting see byte-identical query results.
export const DEFAULT_WEEK_START_DAY: WeekStartDay = 'monday';

/** JS `Date.getDay()` index (0 = Sunday .. 6 = Saturday) for each day. */
export const WEEK_START_DAY_INDEX: Record<WeekStartDay, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

export function isWeekStartDay(value: unknown): value is WeekStartDay {
  return typeof value === 'string' && (WEEK_START_DAYS as readonly string[]).includes(value);
}

/**
 * Resolve the effective week start day from a (possibly absent) financials
 * record. GET /company/:id/financials returns null until first save, so every
 * consumer must fall back to the product default.
 */
export function resolveWeekStartDay(financials: { weekStartDay?: string | null } | null | undefined): WeekStartDay {
  const value = financials?.weekStartDay;
  return isWeekStartDay(value) ? value : DEFAULT_WEEK_START_DAY;
}

/**
 * The Cube granularity to request/expect for weekly bucketing. 'monday' maps
 * to the built-in 'week' granularity; other days map to the custom
 * granularities defined in cube/utils/weekGranularities.js. This name is
 * also the suffix of the result-row key (`<cube>.<dimension>.<granularity>`).
 */
export function weekGranularityName(day: WeekStartDay): string {
  return day === 'monday' ? 'week' : `week_${day}`;
}

/**
 * Days to subtract from `dayOfWeek` (a `Date.getDay()` value) to reach the
 * most recent occurrence of the week start day.
 */
export function daysSinceWeekStart(dayOfWeek: number, weekStartDay: WeekStartDay): number {
  return (dayOfWeek - WEEK_START_DAY_INDEX[weekStartDay] + 7) % 7;
}
